import { useEffect } from 'react';
import { varAlpha } from 'minimal-shared/utils';
import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import CardMedia from '@mui/material/CardMedia';
import IconButton from '@mui/material/IconButton';

import { Image } from 'src/components/image';
import { Iconify } from 'src/components/iconify';
import { Lightbox, useLightBox } from 'src/components/lightbox';
import {
  Carousel,
  useCarousel,
  CarouselThumb,
  CarouselThumbs,
  CarouselArrowNumberButtons,
} from 'src/components/carousel';

// ----------------------------------------------------------------------

export function ProductDetailsCarousel({ slides }) {
  const muted = useBoolean(true);

  const lightbox = useLightBox(slides);

  const carousel = useCarousel({
    thumbs: {
      slidesToShow: 'auto',
    },
  });

  useEffect(() => {
    if (lightbox.open) carousel.mainApi?.scrollTo(lightbox.selected, true);
  }, [carousel.mainApi, lightbox.open, lightbox.selected]);

  return (
    <>
      <div>
        <Box sx={{ mb: 2.5, position: 'relative' }}>
          <Carousel carousel={carousel} sx={{ borderRadius: 2 }}>
            {slides.map(({ src, type }, index) =>
              type.includes('video') ? (
                <Box key={index} sx={{ position: 'relative' }}>
                  <CardMedia
                    loop
                    autoPlay
                    src={src}
                    controls={false}
                    component="video"
                    muted={muted.value}
                    onClick={() => lightbox.onOpen(src)}
                    sx={{ cursor: 'zoom-in', aspectRatio: '1/1' }}
                  />
                  <IconButton
                    size="small"
                    onClick={muted.onToggle}
                    sx={[
                      (theme) => ({
                        p: 1,
                        bottom: 15,
                        right: 120,
                        color: 'common.white',
                        position: 'absolute',
                        bgcolor: varAlpha(theme.vars.palette.grey['900Channel'], 0.48),
                        '&:hover': {
                          bgcolor: varAlpha(theme.vars.palette.grey['900Channel'], 0.56),
                        },
                      }),
                    ]}
                  >
                    <Iconify
                      icon={`solar:volume-${muted.value ? 'cross' : 'loud'}-bold`}
                      sx={{ opacity: muted.value ? 0.7 : 1, '&:hover': { opacity: 1 } }}
                    />
                  </IconButton>
                </Box>
              ) : (
                <Image
                  key={index}
                  alt={src}
                  src={src}
                  ratio="1/1"
                  onClick={() => lightbox.onOpen(src)}
                  sx={{ cursor: 'zoom-in' }}
                />
              )
            )}
          </Carousel>

          <CarouselArrowNumberButtons
            {...carousel.arrows}
            options={carousel.options}
            totalSlides={carousel.dots.dotCount}
            selectedIndex={carousel.dots.selectedIndex + 1}
            sx={{ right: 16, bottom: 16, position: 'absolute' }}
          />
        </Box>

        <CarouselThumbs
          ref={carousel.thumbs.thumbsRef}
          options={carousel.options?.thumbs}
          slotProps={{ disableMask: true, container: { justifyContent: 'center' } }}
          sx={{ width: 1 }}
        >
          {slides.map((item, index) => (
            <CarouselThumb
              index={index}
              key={item.src}
              src={item.src}
              type={item.type}
              selected={index === carousel.thumbs.selectedIndex}
              onClick={() => carousel.thumbs.onClickThumb(index)}
            />
          ))}
        </CarouselThumbs>
      </div>

      <Lightbox
        slides={slides}
        open={lightbox.open}
        close={lightbox.onClose}
        index={lightbox.selected}
        onGetCurrentIndex={(index) => lightbox.setSelected(index)}
      />
    </>
  );
}
